import { computed, Injectable, signal, WritableSignal } from '@angular/core';
import { catchError, EMPTY, tap } from 'rxjs';

import { ApiService } from '../../../core/api/api.service';
import { FileNotification } from '../models/responses/file-notification.model';

@Injectable({
  providedIn: 'root'
})
export class FileNotificationService {
  private _notifications: WritableSignal<FileNotification[]> = signal([]);
  private _loading: WritableSignal<boolean> = signal(false);

  public readonly notifications = this._notifications.asReadonly();
  public readonly loading = this._loading.asReadonly();

  public readonly unreadCount = computed(() =>
    this._notifications().filter(n => !n.viewed).length
  );

  public readonly unreadLabel = computed(() => {
    const count = this.unreadCount();
    if (count === 0) return '';
    return count > 99 ? '99+' : String(count);
  });

  constructor(private apiService: ApiService) {}

  public fetchNotifications(): void {
    this._loading.set(true);
    this.apiService.getFileNotifications()
      .pipe(
        tap(response => this._notifications.set(response.data ?? [])),
        catchError(() => EMPTY)
      )
      .subscribe({ complete: () => this._loading.set(false) });
  }

  public markAsViewed(id: number): void {
    this.apiService.markFileNotificationViewed(id)
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        this._notifications.update(list =>
          list.map(n => n.id === id ? { ...n, viewed: true } : n)
        );
      });
  }

  public markAllAsViewed(): void {
    const unread = this._notifications().filter(n => !n.viewed);
    unread.forEach(n => {
      this.apiService.markFileNotificationViewed(n.id)
        .pipe(catchError(() => EMPTY))
        .subscribe();
    });
    this._notifications.update(list => list.map(n => ({ ...n, viewed: true })));
  }

  public folderPath(notification: FileNotification): string {
    return notification.filePath.substring(0, notification.filePath.lastIndexOf('/'));
  }

  public formatDate(isoDate: string): string {
    return new Date(isoDate).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).replace(',', ' às');
  }
}
